import React, { useState } from 'react';
import WorkspaceShell from './WorkspaceShell';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import {
  useGetCrmLeadsQuery,
  useCreateCrmLeadMutation,
  useUpdateCrmLeadMutation,
  useDeleteCrmLeadMutation,
  useGetCrmRemindersQuery,
  useCreateCrmReminderMutation,
  useUpdateCrmReminderMutation,
} from '../../features/api/apiSlice';

const STATUS_OPTS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'viewing', label: 'Viewing' },
  { value: 'negotiating', label: 'Negotiating' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

const TYPE_OPTS = [
  { value: 'buyer', label: 'Buyer' },
  { value: 'seller', label: 'Seller' },
  { value: 'renter', label: 'Renter' },
  { value: 'investor', label: 'Investor' },
  { value: 'other', label: 'Other' },
];

const CrmPage = () => {
  const { data: leads = [], isLoading, refetch } = useGetCrmLeadsQuery();
  const { data: reminders = [], refetch: refetchRem } = useGetCrmRemindersQuery({ status: 'pending' });
  const [createLead, { isLoading: saving }] = useCreateCrmLeadMutation();
  const [updateLead] = useUpdateCrmLeadMutation();
  const [deleteLead] = useDeleteCrmLeadMutation();
  const [createReminder] = useCreateCrmReminderMutation();
  const [updateReminder] = useUpdateCrmReminderMutation();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    lead_type: 'buyer',
    status: 'new',
    interested_property_ref: '',
    notes: '',
    next_follow_up_at: '',
    priority: 2,
  });
  const [remForm, setRemForm] = useState({ title: '', remind_at: '', lead_id: '', notes: '' });
  const [err, setErr] = useState('');

  const submitLead = async (e) => {
    e.preventDefault();
    setErr('');
    try {
      await createLead({
        ...form,
        next_follow_up_at: form.next_follow_up_at || null,
      }).unwrap();
      setShowForm(false);
      setForm({
        contact_name: '', contact_email: '', contact_phone: '', lead_type: 'buyer',
        status: 'new', interested_property_ref: '', notes: '', next_follow_up_at: '', priority: 2,
      });
      refetch();
    } catch (e2) {
      setErr(e2?.data?.message || 'Failed to save lead');
    }
  };

  const submitReminder = async (e) => {
    e.preventDefault();
    try {
      await createReminder({
        title: remForm.title,
        remind_at: remForm.remind_at,
        notes: remForm.notes,
        lead_id: remForm.lead_id || null,
      }).unwrap();
      setRemForm({ title: '', remind_at: '', lead_id: '', notes: '' });
      refetchRem();
      refetch();
    } catch (e2) {
      setErr(e2?.data?.message || 'Failed to set reminder');
    }
  };

  return (
    <WorkspaceShell
      title="Built-in CRM"
      subtitle="Manage leads, track which client cares about which property, and set follow-up reminders."
    >
      {err && <p className="text-error text-sm mb-3">{err}</p>}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="font-semibold text-lg">Leads</h2>
            <Button size="sm" onClick={() => setShowForm(!showForm)}>{showForm ? 'Close' : 'Add lead'}</Button>
          </div>

          {showForm && (
            <form onSubmit={submitLead} className="rounded-xl border border-border bg-card p-4 space-y-3">
              <Input label="Contact name" required value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
              <div className="grid sm:grid-cols-2 gap-3">
                <Input label="Email" type="email" value={form.contact_email}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
                <Input label="Phone" value={form.contact_phone}
                  onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
                <Select label="Type" options={TYPE_OPTS} value={form.lead_type}
                  onChange={(v) => setForm({ ...form, lead_type: v })} />
                <Select label="Status" options={STATUS_OPTS} value={form.status}
                  onChange={(v) => setForm({ ...form, status: v })} />
              </div>
              <Input label="Interested property ref" value={form.interested_property_ref}
                onChange={(e) => setForm({ ...form, interested_property_ref: e.target.value })}
                placeholder="Listing ID or address" />
              <Input label="Next follow-up" type="datetime-local" value={form.next_follow_up_at}
                onChange={(e) => setForm({ ...form, next_follow_up_at: e.target.value })} />
              <textarea className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm min-h-[70px]"
                placeholder="Notes" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              <Button type="submit" loading={saving} disabled={saving}>Save lead</Button>
            </form>
          )}

          {isLoading ? (
            <p className="text-muted-foreground">Loading leads…</p>
          ) : leads.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground">
              No leads yet. Add your first client interest.
            </div>
          ) : (
            <div className="space-y-2">
              {leads.map((lead) => (
                <div key={lead.id} className="rounded-xl border border-border bg-card p-4">
                  <div className="flex flex-wrap justify-between gap-2">
                    <div>
                      <p className="font-semibold">{lead.contact_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {lead.lead_type} · {lead.contact_phone || lead.contact_email || 'No contact'}
                      </p>
                    </div>
                    <Select
                      options={STATUS_OPTS}
                      value={lead.status}
                      onChange={(v) => updateLead({ id: lead.id, status: v })}
                    />
                  </div>
                  {lead.interested_property_ref && (
                    <p className="text-sm mt-2">Property interest: <strong>{lead.interested_property_ref}</strong></p>
                  )}
                  {lead.next_follow_up_at && (
                    <p className="text-xs text-warning mt-1">
                      Follow-up: {new Date(lead.next_follow_up_at).toLocaleString()}
                    </p>
                  )}
                  {lead.notes && <p className="text-sm text-muted-foreground mt-1">{lead.notes}</p>}
                  <button
                    type="button"
                    className="text-xs text-error mt-2 underline"
                    onClick={() => deleteLead(lead.id)}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <h2 className="font-semibold text-lg">Follow-up reminders</h2>
          <form onSubmit={submitReminder} className="rounded-xl border border-border bg-card p-4 space-y-3">
            <Input label="Title" required value={remForm.title}
              onChange={(e) => setRemForm({ ...remForm, title: e.target.value })} />
            <Input label="Remind at" type="datetime-local" required value={remForm.remind_at}
              onChange={(e) => setRemForm({ ...remForm, remind_at: e.target.value })} />
            <Select
              label="Link to lead (optional)"
              options={[{ value: '', label: 'None' }, ...leads.map((l) => ({ value: String(l.id), label: l.contact_name }))]}
              value={remForm.lead_id}
              onChange={(v) => setRemForm({ ...remForm, lead_id: v })}
            />
            <Button type="submit" size="sm" fullWidth>Add reminder</Button>
          </form>

          <div className="space-y-2">
            {reminders.length === 0 && (
              <p className="text-sm text-muted-foreground">No pending reminders.</p>
            )}
            {reminders.map((r) => (
              <div key={r.id} className="rounded-lg border border-border p-3 text-sm">
                <p className="font-medium">{r.title}</p>
                <p className="text-xs text-muted-foreground">{new Date(r.remind_at).toLocaleString()}</p>
                {r.lead && <p className="text-xs">Lead: {r.lead.contact_name}</p>}
                <Button size="sm" variant="outline" className="mt-2"
                  onClick={() => updateReminder({ id: r.id, status: 'done' })}>
                  Mark done
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </WorkspaceShell>
  );
};

export default CrmPage;
